import { EntityName } from '@/types/editableField';
import type { RawEntityPropertyDefinition } from '@/types/eds/RawDefinitions';
import type { EDSV2OverrideRenderDefinitionResponse } from '@/types/eds/api/responses/EDSV2Response';
import type { NormalizedV2Property } from '@/types/eds/api/responses/NormalizedV2Response';
import { EDSFieldValidationState } from '@/features/eds/types/EDSFieldValidationState';
import type { EDSFilterPrimitiveValue } from '@/features/eds/types/EDSFilterConfig';
import type {
  EdsValidationResponse,
  EdsValidationSetValueResult,
} from '@/features/eds/types/api/responses/EdsValidationResponse';
import type { NormalizedValidationResult } from '@/features/eds/types/normalizedValidationResult';
import type { Palette } from '@mui/material';
import { EdsValidationMessageType } from '@/features/eds/enums/EdsValidationMessageType';
import { EdsValidationEvents } from '@/features/eds/events';
import { EdsRawDefinitionsService } from '@/features/eds/services/EdsRawDefinitionsService/EdsRawDefinitionsService';
import EventService from '@/services/EventService/EventService';

export class EdsValidationService {
  public static normalizeValidationResponse = (response: EdsValidationResponse): NormalizedValidationResult[] => {
    return response.validationResults.map((validationResult) => ({
      proceedOnFailure: validationResult.proceedOnFailure,
      entityName: validationResult.entityName,
      entityId: validationResult.entityId,
      entityField: validationResult.fieldName,
      message: validationResult.message,
      messageType: validationResult.messageLevel,
    }));
  };

  public static dispatchSetValues = (changes: EdsValidationSetValueResult[]): void => {
    changes.forEach(({ propertyName, ...changeData }) => {
      EventService.dispatch(
        new EdsValidationEvents.SetField({
          ...changeData,
          fieldName: propertyName,
        }),
      );
    });
  };

  public static dispatchOverrides = (overrides: EDSV2OverrideRenderDefinitionResponse[]): void => {
    overrides.forEach(({ entityName, entityId, properties }) => {
      // eslint-disable-next-line testing-library/render-result-naming-convention
      const normalizedProperties =
        EdsRawDefinitionsService.normalizePartialOverrideRenderDefinitionProperties(properties);

      normalizedProperties.forEach(({ fieldName, ...override }: Partial<NormalizedV2Property>) => {
        if (entityId && fieldName && entityName)
          EventService.dispatch(
            new EdsValidationEvents.OverrideFieldDefinitions({
              entityId,
              entityName,
              fieldName,
              override,
            }),
          );
      });
    });
  };

  public static getPriorityFieldValidationState(
    validationResultList: NormalizedValidationResult[],
  ): EDSFieldValidationState {
    if (
      validationResultList.find((validationResult) => validationResult.messageType === EdsValidationMessageType.Error)
    ) {
      return EDSFieldValidationState.Error;
    }

    if (
      validationResultList.find((validationResult) => validationResult.messageType === EdsValidationMessageType.Warning)
    ) {
      return EDSFieldValidationState.Warning;
    }

    if (
      validationResultList.find((validationResult) => validationResult.messageType === EdsValidationMessageType.Info)
    ) {
      return EDSFieldValidationState.Info;
    }

    return EDSFieldValidationState.Default;
  }

  public static getValidationColor(validationState: EDSFieldValidationState, palette: Palette): string {
    if (validationState === EDSFieldValidationState.Error) {
      return palette.validation.error;
    }

    if (validationState === EDSFieldValidationState.Warning) {
      return palette.validation.warning;
    }

    if (validationState === EDSFieldValidationState.Info) {
      return palette.validation.info;
    }

    return 'inherit';
  }

  public static prepareOverrideFields(
    overrideResults: EDSV2OverrideRenderDefinitionResponse[],
    config?: { filtering?: { settingKey: string; value: EDSFilterPrimitiveValue } },
  ): { entityId: number; entityName: EntityName; property: Partial<RawEntityPropertyDefinition> }[] {
    return overrideResults.reduce((acc, override) => {
      const properties = override.properties || [];
      const filteredProperties = config?.filtering?.settingKey
        ? properties.filter((property) => property[config.filtering!.settingKey] === config?.filtering?.value)
        : properties;

      return [
        ...acc,
        ...filteredProperties.map((property) => ({
          entityId: override.entityId,
          entityName: override.entityName,
          property,
        })),
      ];
    }, [] as any);
  }
}
